import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useFetchICEAll,
  useUpdateICE,
} from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { ICE } from '@/shared/interfaces/erp';
import { useUiConfirmModalStore } from '@/store/ui';
import { PermissionsEnum } from '@/auth/interfaces';


export type ICEPageProps = {};

const ICEPage: React.FC<ICEPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_ice)

  const returnUrlICEPage = ROUTER_PATHS.administration.ICENav

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );
  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: ICEPagingRes,
    isLoading,
    isRefetching,
  } = useFetchICEAll({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updateStateIce = useUpdateICE({
    enableRedirect: false,
  });

  ///* columns
  const columns = useMemo<MRT_ColumnDef<ICE>[]>(
    () => [
     /*  {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      }, */
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 270,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      ///* fk
      {
        accessorKey: 'numero_referencia', // to allow filtering
        header: 'Número de Referencia',
        size: 200,
        Cell: ({ row }) => row.original?.numero_referencia || 'N/A',
      },
      {
        accessorKey: 'sri_codigo',
        header: 'sri codigo',
        size: 200,
        Cell: ({ row }) => row.original?.sri_codigo || 'N/A',
      },
      {
        accessorKey: 'sri_codigo_porcentaje',
        header: 'SRI codigo porcentaje',
        size: 200,
        Cell: ({ row }) => row.original?.sri_codigo_porcentaje || 'N/A',
      },
      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado!}
            titulo={row.original.estado ? 'Activo' : 'Inactivo'}
            setEstadoSwitch={ice => {
              if (!hasPermission(PermissionsEnum.api_change_ice)) return;
              setConfirmDialog({
                isOpen: true,
                title: 'Cambiar Estado',
                subtitle:
                  '¿Está seguro que desea cambiar el estado de este registro ?',
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateStateIce.mutate({
                    id: row.original.id_ice!,
                    data: {
                      ...row.original,
                      estado: ice as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (row: ICE) => {
    navigate(`${returnUrlICEPage}/editar/${row.id_ice}`);
  };

  return (
    <SingleTableBoxScene
      title="ICE"
      urlRedirect={`${returnUrlICEPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_ice)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        cleanFilter={cleanFilter}
        text="por nombre"
      />

      <GeneralTable<ICE>
        columns={columns}
        data={ICEPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={ICEPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_ice,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_ice,
        ])}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default ICEPage;